import { QBS_DETAILS_DATA_TEST_ID } from '@e2e/constants/exam-management';
import { findLevelItemOrderById } from '@e2e/utils';
import { QBankStructureLevelCategory, QBankStructureStatus } from '@exam-management/graphql';
import { APP_PERMISSIONS } from '@exam-management/permissions';
import { useQBankStructure } from '@exam-management/qbs-provider';
import { Flex } from '@shared/components';
import { useHasPermission } from '@shared/permissions';
import { useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';

import { QBankStructureLevelItemRow } from '../../components';
import { QBankStructureLevelItemFormAction } from '../../constants';
import { useEditingField } from '../../hooks';
import {
  NON_INFO_NAME_PLACEHOLDER,
  NON_NAME_PLACEHOLDER,
  useEditQBSLevelItem,
  useQBankStructureLevelFormContext,
} from '../../providers';
import type { ActionOutletContextType } from '../../types';
import { findNextLevelCategory } from '../qbank-structure-level-item-list/utils';

import AddNewLevelItemButton from './add-new-level-item-button';
import RowEndAdornment from './end-adornment';
import QBankStructureLevelItemRowHeader from './qbank-structure-level-item-row-header-with-action';
import RowStartAdornment from './start-adornment';
import SubtopicErrorTooltip from './subtopic-error-tooltip';
import {
  getEndAdornmentState,
  getIsChildAddable,
  getRowItemState,
  getStartAdornmentState,
  QBankStructureLevelItemRowWithActionState,
} from './utils';

interface QBankStructureLevelItemRowWithActionProps {
  id: string;
  expanded?: boolean;
}

export default function QBankStructureLevelItemRowWithAction(
  props: Readonly<QBankStructureLevelItemRowWithActionProps>,
) {
  const { id, expanded: isExpanded = false } = props;

  const {
    qBankStructureInformation,
    qBankStructureLevels,
    validatePublishQBankStructure: { missingLastItemErrors: validateErrors },
  } = useQBankStructure();

  const {
    levelItems: qBankStructureLevelItems,
    addChildOfQBankStructureLevelItem,
    duplicateQBankStructureLevelItem,
    deleteQBankStructureLevelItem,
    updateQBankStructureLevelItemToNonInfoItem,
    addQBankStructureLevelItemsAbility,
  } = useEditQBSLevelItem();

  const { setValue, unregister } = useQBankStructureLevelFormContext();
  const { action: listAction } = useOutletContext<ActionOutletContextType>();
  const editingField = useEditingField();
  const isUpdatable = useHasPermission(APP_PERMISSIONS.QBANK.STRUCTURE.UPDATE);

  const qBankStructureLevelItem = useMemo(
    () => qBankStructureLevelItems.find((item) => item.id === id),
    [qBankStructureLevelItems],
  );

  if (!qBankStructureLevelItem) {
    return null;
  }

  const { label: nextLevelCategoryLabel, category: nextLevelCategory } = findNextLevelCategory(
    qBankStructureLevels,
    qBankStructureLevelItem.qBankStructureLevel.id,
  );

  const isFirstLevel =
    qBankStructureLevelItem.qBankStructureLevel.level ===
    Math.min(...qBankStructureLevels.map((level) => level.level));

  const isLastLevel =
    qBankStructureLevelItem.qBankStructureLevel.level ===
    Math.max(...qBankStructureLevels.map((level) => level.level));

  const order = isFirstLevel ? qBankStructureInformation.code : qBankStructureLevelItem.order;
  const isNew = !!qBankStructureLevelItem.isNew;

  const rowItemState = isFirstLevel
    ? QBankStructureLevelItemRowWithActionState.VIEW
    : getRowItemState(listAction, qBankStructureInformation.status);

  const isDraft = qBankStructureInformation.status === QBankStructureStatus.Draft;
  const isNewOnPending =
    isNew && qBankStructureInformation.status === QBankStructureStatus.Unpublished;

  // QBank structure status DRAFT is considered as always in editing action
  const isEditingList =
    qBankStructureInformation.status === QBankStructureStatus.Draft ||
    listAction !== QBankStructureLevelItemFormAction.VIEW;

  const isEditable = rowItemState === QBankStructureLevelItemRowWithActionState.EDIT && isUpdatable;

  const isNonInfo = qBankStructureLevelItem.order === 0;
  const hasNonInfo = qBankStructureLevelItems.some(
    (item) =>
      item.order === 0 &&
      item.qBankStructureLevelId === qBankStructureLevelItem.qBankStructureLevelId &&
      item.parentLevelItemId === qBankStructureLevelItem.parentLevelItemId,
  );

  const startAdornmentActionState = getStartAdornmentState(
    isDraft,
    isNew,
    isEditable,
    rowItemState,
    isFirstLevel,
  );
  const endAdornmentActionState = getEndAdornmentState(
    isDraft,
    isNew,
    isEditable,
    rowItemState,
    isFirstLevel,
  );

  const isChildAddable = getIsChildAddable(
    qBankStructureInformation.status,
    isLastLevel,
    isEditingList,
  );

  const placeholder = isNonInfo ? NON_INFO_NAME_PLACEHOLDER : NON_NAME_PLACEHOLDER;

  const handleAddChild = () => {
    addChildOfQBankStructureLevelItem(id, qBankStructureLevels, qBankStructureInformation.id);
  };

  const handleDuplicate = () => {
    duplicateQBankStructureLevelItem(id);
  };

  const handleDelete = () => {
    deleteQBankStructureLevelItem(id);
    unregister(id);
    editingField.handler.removeField(id);
  };

  const handleSetNonInfo = () => {
    setValue(id, null);
    updateQBankStructureLevelItemToNonInfoItem(id, qBankStructureLevels);
  };

  const dataTestIdIdentifier = findLevelItemOrderById(
    qBankStructureLevelItems,
    id,
    qBankStructureInformation.code,
  );

  const defaultNameValue = isFirstLevel ? qBankStructureInformation.name : undefined;

  const hasSubTopicError = validateErrors?.find((error) => error.name === id);

  const isChangeableToNonInfo =
    qBankStructureLevelItem.qBankStructureLevel.category !== QBankStructureLevelCategory.SubTopic;

  const isDuplicable = Boolean(
    qBankStructureLevelItem.parentLevelItemId &&
      (addQBankStructureLevelItemsAbility.get(qBankStructureLevelItem.parentLevelItemId) ||
        isDraft),
  );

  const isEnableAddNextLevel = Boolean(
    isChildAddable &&
      isUpdatable &&
      nextLevelCategory &&
      (addQBankStructureLevelItemsAbility.get(qBankStructureLevelItem.id) || isDraft),
  );

  const AddItemButton = isEnableAddNextLevel ? (
    <AddNewLevelItemButton
      data-testid={`${QBS_DETAILS_DATA_TEST_ID.LEVEL_ITEM_ROW}-${dataTestIdIdentifier}-add-level-item-button`}
      sx={{
        minWidth: '112px',
        justifyContent: 'flex-start',
        alignSelf: 'center',
      }}
      onClick={handleAddChild}
    >
      {nextLevelCategoryLabel}
    </AddNewLevelItemButton>
  ) : null;

  if (isFirstLevel) {
    return (
      <QBankStructureLevelItemRowHeader
        name={id}
        order={order}
        category={qBankStructureLevelItem.qBankStructureLevel.category}
        defaultNameValue={defaultNameValue}
        endAdornment={AddItemButton}
      />
    );
  }

  return (
    <QBankStructureLevelItemRow
      name={id}
      category={qBankStructureLevelItem.qBankStructureLevel.category}
      defaultNameValue={defaultNameValue}
      order={order}
      placeholder={placeholder}
      dataTestId={`${QBS_DETAILS_DATA_TEST_ID.LEVEL_ITEM_ROW}-${dataTestIdIdentifier}`}
      expanded={isExpanded}
      editable={isEditable && (isNewOnPending || isDraft)}
      nonInfo={isNonInfo}
      startAdornment={
        <RowStartAdornment
          state={startAdornmentActionState}
          categoryExpandState={{
            category: qBankStructureLevelItem.qBankStructureLevel.category,
            expanded: isExpanded,
          }}
        />
      }
      endAdornment={
        <Flex sx={{ alignItems: 'center', gap: (theme) => theme.spacing(2) }}>
          <SubtopicErrorTooltip error={!!hasSubTopicError} />
          {AddItemButton}
          {isUpdatable ? (
            <RowEndAdornment
              duplicable={isDuplicable}
              dataTestId={`${QBS_DETAILS_DATA_TEST_ID.LEVEL_ITEM_ROW}-${dataTestIdIdentifier}-menu`}
              state={endAdornmentActionState}
              hasNonInfo={hasNonInfo}
              qBSStatus={qBankStructureInformation.status}
              isNew={isNew}
              eventHandlers={{
                onDuplicate: handleDuplicate,
                onDelete: handleDelete,
                onSetNonInfo: isChangeableToNonInfo ? handleSetNonInfo : undefined,
              }}
              categoryExpandState={{
                category: qBankStructureLevelItem.qBankStructureLevel.category,
                expanded: isExpanded,
              }}
            />
          ) : null}
        </Flex>
      }
      wrapperProps={{
        sx: {
          width: '100%',
        },
      }}
    />
  );
}
